/**
 * Contextual Modify tab — one-shot action backends for Walls & Surfaces.
 *
 * Composed by useContextualActions(); each action reads the current selection,
 * mutates the model through bimStore (undoable kernel transactions) and gives
 * BIM-style toast feedback. Pure data operations, no viewport interaction
 * (those live in tools/contextual/ElementEditTools.ts).
 */
import { useCallback } from 'react';
import { BIMElement, WallElement, WallJoinType } from '../../core/types';
import { useBIMStore } from '../../state/useBIMStore';
import { useAppStore } from '../../state/store';
import { notify } from '../../utils/notify';
import { getSelectedElements, elementWorldBounds } from '../../utils/contextualModelUtils';
import { WallJoinSolver } from '../../core/WallJoinSolver';
import { processWallJoins } from '../../tools/WallTool';
import i18n from '../../i18n';

/** Categories a wall can attach its top/base to. */
const ATTACH_TARGETS = new Set(['Roofs', 'Floors', 'Ceilings', 'Toposolid']);

/**
 * Keep wall geometry in sync after a manual profile/join edit: recompute the
 * join profiles exactly like the placement pipeline does.
 */
function recomputeWallJoins(walls: WallElement[]) {
    const bim = useBIMStore.getState();
    const all = Array.from(bim.project.elements.values()).filter(e => e.category === 'Walls') as WallElement[];

    walls.forEach(wall => {
        const current = bim.project.elements.get(wall.id) as WallElement | undefined;
        if (!current) return;
        const neighbors = all.filter(w => w.id !== current.id);
        // processWallJoins only accepts the joinable styles; 'None' falls back
        // to the plain butt default.
        const joinStyle = current.geometryData.joinConfiguration?.startJoinType ?? 'Miter';
        const joins = processWallJoins(current, neighbors, joinStyle === 'None' ? 'Butt' : joinStyle);
        const connected = neighbors.filter(w => joins.some(j => j.targetId === w.id));
        if (connected.length === 0) return;
        const g = current.geometryData;
        bim.updateElement(current.id, {
            constraints: joins.map(j => ({ ...j })),
            geometryData: {
                ...g,
                profile: WallJoinSolver.computeWallProfile(current, connected),
                layerProfiles: WallJoinSolver.computeLayerProfiles(current, connected),
            },
        } as any);
    });
}

/* ============================ Walls ============================ */

export function useWallContextualActions() {
    /** Reset Profile: drop the custom profile back to the plain rectangle. */
    const resetProfile = useCallback(() => {
        const walls = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (walls.length === 0) {
            notify(i18n.t('tools.notify.selectAWallToResetIts'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        walls.forEach(wall => {
            bim.updateElement(wall.id, {
                geometryData: {
                    ...wall.geometryData,
                    profile: undefined,
                    layerProfiles: undefined,
                    elevationProfile: undefined,
                },
            } as any);
        });
        recomputeWallJoins(walls);
        notify(i18n.t('tools.notify.profileReset', { count: walls.length }), 'success');
    }, []);

    /** Find the attach target (roof/floor/ceiling/toposolid) near the wall. */
    const findAttachTarget = (wall: WallElement, location: 'Top' | 'Bottom') => {
        const bim = useBIMStore.getState();
        const wallBounds = elementWorldBounds(wall);
        if (!wallBounds) return null;
        const anchorZ = location === 'Top' ? wallBounds.max.z : wallBounds.min.z;
        const cx = (wallBounds.min.x + wallBounds.max.x) / 2;
        const cy = (wallBounds.min.y + wallBounds.max.y) / 2;

        let best: { id: string; z: number; name: string } | null = null;
        Array.from(bim.project.elements.values()).forEach(el => {
            if (!ATTACH_TARGETS.has(el.category)) return;
            const b = elementWorldBounds(el);
            if (!b) return;
            // Target surface must span the wall position in plan.
            if (cx < b.min.x - 0.01 || cx > b.max.x + 0.01 || cy < b.min.y - 0.01 || cy > b.max.y + 0.01) return;
            const z = location === 'Top' ? b.max.z : b.min.z;
            const forward = location === 'Top' ? z >= anchorZ - 0.05 : z <= anchorZ + 0.05;
            if (!forward) return;
            const dist = Math.abs(z - anchorZ);
            if (!best || dist < Math.abs(best.z - anchorZ)) {
                best = { id: el.id, z, name: el.name ?? el.type };
            }
        });
        return best as { id: string; z: number; name: string } | null;
    };

    /** Attach Top / Attach Base: stretch the wall to the nearest surface. */
    const attach = useCallback((location: 'Top' | 'Bottom') => {
        const walls = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (walls.length === 0) {
            notify(i18n.t('tools.notify.wallAttachSelect', { position: location === 'Top' ? i18n.t('tools.notify.posTop') : i18n.t('tools.notify.posBase') }), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        let attached = 0;
        walls.forEach(wall => {
            const target = findAttachTarget(wall, location);
            if (!target) {
                notify(i18n.t('tools.notify.noAttachableSurface', { wall: wall.name ?? 'a wall' }), 'warning');
                return;
            }
            const g = wall.geometryData;
            const baseZ = g.baseOffset || 0;
            const attachments = [...(g.attachments ?? []).filter(a => a.location !== location), { targetId: target.id, location }];
            if (location === 'Top') {
                const newHeight = Math.max(0.1, target.z - baseZ);
                bim.updateElement(wall.id, {
                    geometryData: { ...g, height: newHeight, topConstraintId: target.id, attachments },
                } as any);
            } else {
                const newBase = target.z;
                const newHeight = Math.max(0.1, (baseZ + (g.height ?? 3)) - newBase);
                bim.updateElement(wall.id, {
                    geometryData: { ...g, baseOffset: newBase, height: newHeight, attachments },
                } as any);
            }
            attached++;
        });
        if (attached > 0) notify(i18n.t('tools.notify.wallAttached', { position: location === 'Top' ? i18n.t('tools.notify.posTop') : i18n.t('tools.notify.posBase'), count: attached }), 'success');
    }, []);

    const attachTop = useCallback(() => attach('Top'), [attach]);
    const attachBase = useCallback(() => attach('Bottom'), [attach]);

    /** Detach Top / Detach Base: remove the attachment record. */
    const detach = useCallback((location: 'Top' | 'Bottom') => {
        const walls = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (walls.length === 0) {
            notify(i18n.t('tools.notify.wallDetachSelect', { position: location === 'Top' ? i18n.t('tools.notify.posTop') : i18n.t('tools.notify.posBase') }), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        let detached = 0;
        walls.forEach(wall => {
            const g = wall.geometryData;
            if (!(g.attachments ?? []).some(a => a.location === location)) return;
            bim.updateElement(wall.id, {
                geometryData: {
                    ...g,
                    attachments: g.attachments!.filter(a => a.location !== location),
                    topConstraintId: location === 'Top' ? 'Unconnected' : g.topConstraintId,
                },
            } as any);
            detached++;
        });
        notify(
            detached > 0
                ? `Detached ${location === 'Top' ? 'top' : 'base'} of ${detached} wall(s).`
                : `Selected walls are not attached at the ${location === 'Top' ? 'top' : 'base'}.`,
            detached > 0 ? 'success' : 'warning'
        );
    }, []);

    const detachTop = useCallback(() => detach('Top'), [detach]);
    const detachBase = useCallback(() => detach('Bottom'), [detach]);

    /**
     * Wall Joins: cycle the joint style of the selected walls through
     * Butt → Miter → Square off (BIM's Wall Joins dropdown subset) and
     * re-solve the joint profiles.
     */
    const wallJoins = useCallback(() => {
        const walls = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (walls.length < 1) {
            notify(i18n.t('tools.notify.selectWallSToChangeTheir'), 'warning');
            return;
        }
        const order: WallJoinType[] = ['Butt', 'Miter', 'SquareOff', 'None'];
        const bim = useBIMStore.getState();
        const current = walls[0].geometryData.joinConfiguration?.startJoinType ?? 'Butt';
        const next = order[(order.indexOf(current) + 1) % order.length];
        walls.forEach(wall => {
            const g = wall.geometryData;
            bim.updateElement(wall.id, {
                geometryData: {
                    ...g,
                    joinConfiguration: {
                        startJoinType: next,
                        endJoinType: next,
                        startAllowJoin: next !== 'None',
                        endAllowJoin: next !== 'None',
                    },
                    // Reset computed profiles so they are re-solved next.
                    profile: undefined,
                    layerProfiles: undefined,
                },
            } as any);
        });
        recomputeWallJoins(walls);
        notify(i18n.t('tools.notify.wallJoinsSet', { join: next === 'SquareOff' ? i18n.t('tools.notify.joinSquareOff') : next }), 'success');
        useAppStore.getState().setActiveTool('select');
    }, []);

    return { resetProfile, attachTop, attachBase, detachTop, detachBase, wallJoins };
}

/* ============================ Surfaces ============================ */

export function useSurfaceContextualActions() {
    /** Reset Shape: clear all sub-element slopes / split lines on Floors. */
    const resetShape = useCallback(() => {
        const floors = getSelectedElements().filter(e => e.category === 'Floors' && (e as any).geometryData?.shapeEditor);
        if (floors.length === 0) {
            notify(i18n.t('tools.notify.selectAFloorWithShapeModifications'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        floors.forEach(el => {
            const g = (el as any).geometryData;
            const { shapeEditor: _drop, ...rest } = g;
            bim.updateElement(el.id, { geometryData: rest } as any);
        });
        notify(i18n.t('tools.notify.shapeModifiersReset', { count: floors.length }), 'success');
    }, []);

    return { resetShape };
}

/* ============================ Parts & Assemblies ============================ */

/**
 * Create Parts: split each selected wall into per-layer independent elements
 * (BIM's fabrication-parts subset). Doors/windows hosted by parted walls are
 * re-hosted onto the core layer part.
 */
export function useViewContextualActions() {
    const createParts = useCallback(() => {
        const bim = useBIMStore.getState();
        const walls = getSelectedElements().filter(e => e.category === 'Walls') as WallElement[];
        if (walls.length === 0) {
            notify(i18n.t('tools.notify.selectAWallOrFloorTo'), 'warning');
            return;
        }
        const toRemove: string[] = [];
        const toAdd: BIMElement[] = [];
        let parts = 0;

        walls.forEach(wall => {
            const layers = wall.geometryData.layers ?? [];
            if (layers.length === 0) return;
            let coreLayerId: string | null = null;
            layers.forEach((layer, idx) => {
                if (layer.function === 'Structure') coreLayerId = `${wall.id}-part-${idx}`;
                toAdd.push({
                    id: `${wall.id}-part-${idx}` ?? crypto.randomUUID(),
                    category: 'Walls',
                    type: `Part: ${wall.type} — ${layer.function}`,
                    name: `${wall.name ?? 'Wall'} Part ${idx + 1}`,
                    parameters: {
                        ...wall.parameters,
                        'Is Part': true,
                        'Host Wall': wall.name ?? wall.id,
                        'Layer Function': layer.function,
                    },
                    constraints: [],
                    geometryData: {
                        ...wall.geometryData,
                        layers: [layer],
                        thickness: layer.thickness,
                        profile: undefined,
                        layerProfiles: undefined,
                    },
                } as any);
                parts++;
            });
            toRemove.push(wall.id);

            // Re-host openings onto the structural part so they survive.
            Array.from(bim.project.elements.values()).forEach(child => {
                const hostId = (child as any).geometryData?.hostWallId;
                if (hostId === wall.id) {
                    bim.updateElement(child.id, {
                        geometryData: { ...(child as any).geometryData, hostWallId: coreLayerId ?? wall.id },
                    } as any);
                }
            });
        });

        toRemove.reverse().forEach(id => bim.removeElement(id));
        toAdd.forEach(el => bim.addElement(el));
        bim.setSelection([]);
        useAppStore.getState().select(toAdd.map(e => e.id));
        notify(i18n.t('tools.notify.partsCreated', { count: parts }), 'success');
    }, []);

    /** Create Assembly: bundle the selection into a named assembly group. */
    const createAssembly = useCallback(() => {
        const bim = useBIMStore.getState();
        const els = getSelectedElements();
        if (els.length < 2) {
            notify(i18n.t('tools.notify.selectAtLeastTwoElementsTo'), 'warning');
            return;
        }
        const count = Array.from(bim.project.elements.values())
            .filter(e => (e as any).parameters?.['Is Assembly']).length + 1;
        bim.addElement({
            id: crypto.randomUUID(),
            category: 'Model Groups',
            type: 'Assembly',
            name: `Assembly ${count}`,
            parameters: {
                'Is Assembly': true,
                'Name': `Assembly ${count}`,
                'Members': String(els.length),
            },
            constraints: [],
            geometryData: {
                origin: [0, 0, 0],
                memberIds: els.map(e => e.id),
            },
        } as any);
        notify(i18n.t('tools.notify.assemblyCreated', { name: count, count: els.length }), 'success');
    }, []);

    return { createParts, createAssembly };
}
